import React, { useState } from "react";
import { Sparkles } from "lucide-react";
import { api, apiError } from "../lib/api";
import { useAiModels, PROVIDER_LABEL } from "./ModelPicker";

export function AiQuizDraft({ onDraft }) {
  const models = useAiModels();
  const [model, setModel] = useState("sonnet-5");
  const [topic, setTopic] = useState("");
  const [count, setCount] = useState(5);
  const [difficulty, setDifficulty] = useState("mixed");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const draft = async () => {
    if (topic.trim().length < 3) return setErr("Describe the topic in a few words.");
    setErr(null);
    setBusy(true);
    try {
      const { data } = await api.post("/teacher/quizzes/draft", { topic: topic.trim(), count, difficulty, model });
      onDraft(data);
    } catch (e) {
      setErr(apiError(e, "Could not draft the quiz."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-md border border-fuchsia-400/30 bg-fuchsia-500/5 p-4" data-testid="ai-quiz-draft">
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-fuchsia-300"><Sparkles size={12} /> Draft with AI</div>
      <p className="mt-1 text-xs text-slate-400">Type a topic or lesson (e.g. "Le Chatelier's principle", "Year 2 · Redox titrations"), pick Claude or ChatGPT, and the builder gets filled. You can edit everything before saving.</p>
      <div className="mt-3 flex flex-col gap-2 sm:flex-row">
        <input
          value={topic}
          onChange={(e) => setTopic(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && draft()}
          disabled={busy}
          placeholder="Topic or lesson title"
          data-testid="ai-draft-topic"
          className="min-w-0 flex-1 rounded-md border border-white/10 bg-black/60 px-3 py-2 text-sm text-white placeholder:text-slate-500 focus:border-fuchsia-400/60 focus:outline-none"
        />
        <select value={model} onChange={(e) => setModel(e.target.value)} disabled={busy} data-testid="ai-draft-model" className="rounded-md border border-white/10 bg-black/60 px-3 py-2 text-sm text-slate-200 focus:outline-none">
          {models.map((m) => <option key={m.key} value={m.key}>{PROVIDER_LABEL[m.provider] || m.provider} · {m.label}</option>)}
        </select>
        <select value={count} onChange={(e) => setCount(Number(e.target.value))} disabled={busy} data-testid="ai-draft-count" className="rounded-md border border-white/10 bg-black/60 px-3 py-2 text-sm text-slate-200 focus:outline-none">
          {[3, 5, 8, 10].map((n) => <option key={n} value={n}>{n} questions</option>)}
        </select>
        <select value={difficulty} onChange={(e) => setDifficulty(e.target.value)} disabled={busy} data-testid="ai-draft-difficulty" className="rounded-md border border-white/10 bg-black/60 px-3 py-2 text-sm text-slate-200 focus:outline-none">
          <option value="easy">Easy</option>
          <option value="mixed">Mixed</option>
          <option value="hard">Hard</option>
        </select>
        <button onClick={draft} disabled={busy} data-testid="ai-draft-btn" className="inline-flex items-center justify-center gap-2 rounded-md bg-fuchsia-500 px-4 py-2 text-sm font-semibold text-black hover:bg-fuchsia-400 disabled:opacity-50">
          <Sparkles size={14} /> {busy ? "Drafting…" : "Draft"}
        </button>
      </div>
      {err && <div className="mt-2 rounded-md border border-red-400/40 bg-red-500/10 p-2 text-xs text-red-200" data-testid="ai-draft-error">{err}</div>}
    </div>
  );
}
